import { useEffect, useMemo, useState } from "react";
import { db, type Point, type Brand } from "../db";
import { daysAgo, daysText, dayClass, overdue, typeIcon, pointOwnership } from "../lib";
import { Shell, type ScreenProps } from "./common";

type Row = Point & { last: string | null; brands: string[]; own: boolean };
type TypeFilter = "all" | "pharmacy" | "state" | "private" | "podolog" | "cosmetolog" | "recent";

export default function Points({ updateReady, nav, state, user, onSync }: ScreenProps) {
  const isManager = user.role === "manager";
  const [rows, setRows] = useState<Row[] | null>(null);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState<TypeFilter>("all");
  // Чьи точки показывать: «mine», «all» или id бренда (руководитель смотрит бренд целиком).
  // Сотруднику по умолчанию — свои: общий список наполовину состоит из чужих точек.
  const [scope, setScope] = useState<string>(isManager ? "all" : "mine");

  useEffect(() => {
    void (async () => {
      const [points, visits, own, allBrands] = await Promise.all([
        db.points.toArray(), db.visits.toArray(), pointOwnership(user.id), db.brands.toArray(),
      ]);
      // Последний визит по каждой точке — считаем один раз, а не в цикле отрисовки.
      const last = new Map<string, string>();
      for (const v of visits) {
        if (v.deletedAt) continue;
        const prev = last.get(v.pointId);
        if (!prev || v.visitedAt > prev) last.set(v.pointId, v.visitedAt);
      }
      setBrands(allBrands.filter((b) => !b.deletedAt));
      setRows(points.filter((p) => !p.deletedAt)
        .map((p) => ({ ...p, last: last.get(p.id) ?? null, ...own(p) })));
    })();
  }, [state.lastSync, state.pending, user.id]);

  // Бренд сотрудника. Если не назначен — Lacalut, как и в чек-листе выкладки.
  const myBrand = user.brandId ?? brands.find((b) => b.name === "Lacalut")?.id ?? null;

  // Пока отметки «чей список» не доехали с сервера, делить нечем: показали бы
  // сотруднику только им заведённое, и он решил бы, что остальное пропало.
  const stamped = Boolean(rows?.some((p) => p.brandId));
  const active = stamped ? scope : "all";

  const inScope = (p: Row, s: string) =>
    s === "all" ? true
    : s === "mine" ? p.own || (myBrand != null && p.brands.includes(myBrand))
    : p.brands.includes(s);

  const { list, outside } = useMemo(() => {
    if (!rows) return { list: [] as Row[], outside: 0 };
    const needle = q.trim().toLowerCase();
    const matches = rows
      .filter((p) =>
        filter === "all" ? true
        : filter === "pharmacy" ? p.type === "pharmacy"
        : filter === "state" ? p.type === "dental" && p.ownership === "state"
        : filter === "private" ? p.type === "dental" && p.ownership !== "state"
        : filter === "podolog" ? p.type === "podolog"
        : filter === "cosmetolog" ? p.type === "cosmetolog"
        // «Недавние» нужны, чтобы с телефона быстро найти точку, где был сегодня,
        // и доложить фото: обычная сортировка увозит такие точки в самый низ.
        : (daysAgo(p.last) ?? 9999) <= 7)
      .filter((p) =>
        !needle ||
        (p.name + " " + p.address + " " + p.city + " " + (p.network ?? ""))
          .toLowerCase().includes(needle)
      );
    const found = matches.filter((p) => inScope(p, active));

    return {
      list: filter === "recent"
        ? found.sort((a, b) => (b.last ?? "").localeCompare(a.last ?? ""))
        : found.sort((a, b) => overdue(b, daysAgo(b.last)) - overdue(a, daysAgo(a.last))),
      // Сколько подходящих точек осталось за пределами выбранного — считаем только
      // при поиске: человек ищет конкретную точку и не должен завести её второй раз.
      outside: needle ? matches.length - found.length : 0,
    };
  }, [rows, q, filter, active, myBrand]);

  const count = (s: string) => rows?.filter((p) => inScope(p, s)).length ?? 0;
  const title = active === "mine" ? "Мои точки"
    : active === "all" ? "Все точки"
    : "Точки · " + (brands.find((b) => b.id === active)?.name ?? "");

  return (
    <Shell
      title={title}
      state={state} updateReady={updateReady}
      footer={
        <>
          <button className="btn primary" onClick={() => nav({ name: "pointForm" })}>+ Точка</button>
          <button className="btn" onClick={() => nav({ name: "stock" })}>Мои остатки</button>
          <button className="btn ghost" style={{ marginLeft: "auto" }}
            onClick={() => nav({ name: "profile" })}>Профиль</button>
        </>
      }
    >
      <input
        className="inp"
        placeholder="Поиск по названию или адресу"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        style={{ marginBottom: 12 }}
      />

      {stamped && (
        <div className="seg" style={{ marginBottom: 12 }}>
          {(isManager
            ? [["all", "Все"] as const,
               ...brands.filter((b) => count(b.id) > 0).map((b) => [b.id, b.name] as const)]
            : [["mine", "Мои точки"] as const, ["all", "Все"] as const]
          ).map(([k, label]) => (
            <button key={k} type="button" aria-pressed={active === k} onClick={() => setScope(k)}>
              {label} <span style={{ opacity: .6 }}>{count(k)}</span>
            </button>
          ))}
        </div>
      )}

      <div className="chips">
        {([["all", "Все типы"], ["recent", "🕐 Недавние"], ["pharmacy", "💊 Аптеки"],
           ["private", "🦷 Частные"], ["state", "🏥 Государственные"],
           ["podolog", "🦶 Подологи"], ["cosmetolog", "💆 Косметологи"]] as const)
          .map(([k, label]) => (
            <button key={k} className="chip" aria-pressed={filter === k}
              onClick={() => setFilter(k)}>{label}</button>
          ))}
      </div>

      {rows === null && <div className="empty">Загрузка…</div>}

      {rows !== null && rows.length === 0 && (
        <div className="empty">
          {state.error ? (
            <>
              Не удалось получить данные.<br />
              <span style={{ color: "var(--crit)" }}>{state.error}</span><br />
              <button className="btn tiny" onClick={onSync} style={{ marginTop: 12 }}>
                Попробовать снова
              </button>
            </>
          ) : state.lastSync ? (
            <>Точек пока нет.<br />Заведи первую кнопкой «+ Точка».</>
          ) : (
            <>
              Данные ещё не скачались.<br />
              <button className="btn tiny" onClick={onSync} style={{ marginTop: 12 }}>
                Обновить
              </button>
            </>
          )}
        </div>
      )}

      {rows !== null && rows.length > 0 && list.length === 0 && (
        <div className="empty">
          {filter === "recent" ? "За последнюю неделю визитов не было."
            : active === "mine" && !q.trim() && filter === "all"
              ? "В «Моих» пока пусто. Переключись на «Все» или заведи точку."
            : outside > 0 ? "Среди выбранных не нашлось."
            : "Ничего не нашлось. Измени поиск или добавь новую точку."}
        </div>
      )}

      <div className="list">
        {list.map((p) => {
          const d = daysAgo(p.last);
          return (
            <button key={p.id} className="item" onClick={() => nav({ name: "point", id: p.id })}>
              <span className="ico" aria-hidden="true">{typeIcon(p)}</span>
              <span className="main">
                <span className="name">{p.name}</span>
                <span className="meta">
                  {p.city}{p.address ? ", " + p.address : ""}
                  {p.network ? " · сеть " + p.network : ""}
                </span>
              </span>
              <span className="cat" title="Категория">{p.category}</span>
              <span className={"pill " + dayClass(p, d)}>{daysText(d)}</span>
            </button>
          );
        })}
      </div>

      {outside > 0 && (
        <div className="note" style={{ marginTop: 14 }}>
          Среди остальных точек по этому запросу есть ещё <b>{outside}</b>. Прежде чем
          заводить новую — проверь, нет ли её там.{" "}
          <button className="btn tiny" style={{ marginLeft: 6 }} onClick={() => setScope("all")}>
            Показать
          </button>
        </div>
      )}

      {list.length > 0 && (
        <div className="hint" style={{ marginTop: 14, textAlign: "center" }}>
          Показано точек: {list.length}. Сверху те, где давно не были.
        </div>
      )}
    </Shell>
  );
}
